// @flow
import React, { useState, useMemo } from 'react';
import type { Element } from 'react';

import filters from '@globalConstants/departmentTranslationCodes';

import type { FilterOption } from '@components/MobileFilters/types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faList } from '@fortawesome/free-solid-svg-icons';

import { CTA_BUTTON } from '@components/StatefulButton/constants';

import { MobileFilters, SelectFilter } from '@components/MobileFilters';

import { withTranslation, Router } from '@i18n';
import { DOCTOR_FILTER } from '@routes';
import { useBreakpoint } from '@hooks';
import { connect } from 'react-redux';
import compose from 'lodash/fp/compose';
import { sortQueryParams } from '@lib/helpers';
import Select from './components/Select';
import StyledSelect from './components/StyledSelect';

import {
  StyledWrapper,
  StyledParametersContainer,
  StyledSeparationLine,
  StyledInnerWrapper,
  StyledMobileFiltersButton,
  StyledShowMapFloatingButton
} from './styles';

import type { Props } from './types';
import { requestDoctorListSearchBar } from '../redux/actions';

const SearchBar = ({
  t, toggleMap, isMapVisible, query, SearchList
}: Props): Element<any> => {
  const [isMobileFiltersOpen, setMobileFiltersOpen]: [boolean, Function] = useState(false);
  const isDesktop: boolean = useBreakpoint('desktop');

  const setDepartment = ({ target }: { target?: { value: string } }): void => {
    const params = sortQueryParams({
      ...query,
      department: target?.value || 'all-doctors',
      address_city: query.address_city ? query.address_city : 'everywhere',
      pageNumber: 1
    }, [
      'query',
      'department',
      'address_city',
      'pageNumber'
    ]);
    Router.pushRoute(DOCTOR_FILTER, params);
  };

  const setPosition = ({ target }: { target?: { value: string } }): void => {
    const params = sortQueryParams({
      ...query,
      address_city: target?.value || 'everywhere',
      department: query.department ? query.department : 'all-doctors',
      pageNumber: 1
    }, [
      'query',
      'department',
      'address_city',
      'pageNumber'
    ]);
    Router.pushRoute(DOCTOR_FILTER, params);
  };

  const setDoctor = ({ key }: { key?: { value: string } }): void => {
    const params = sortQueryParams({
      ...query,
      query: key || '',
      address_city: query.address_city ? query.address_city : 'everywhere',
      department: query.department ? query.department : 'all-doctors',
      pageNumber: 1
    }, [
      'query',
      'department',
      'address_city',
      'pageNumber'
    ]);
    Router.pushRoute(DOCTOR_FILTER, params);
  };

  const changeFilters = (values: {department: string, position: string, address_city: string}) => {
    const val = values;
    if (!values.department) {
      val.department = 'all-doctors';
    }
    if (!values.position) {
      val.address_city = 'everywhere';
    } else {
      val.address_city = values.position;
    }
    const params = sortQueryParams({ ...val, pageNumber: 1 }, [
      'query',
      'department',
      'address_city',
      'pageNumber'
    ]);
    Router.pushRoute(DOCTOR_FILTER, params);
  };

  let doctorNames;
  if (SearchList !== undefined) {
    doctorNames = SearchList.map((obj) => {
      const rObj = {};
      rObj.key = obj.attributes.slug;
      rObj.value = obj.attributes.name;
      rObj.label = obj.attributes.name;
      return rObj;
    });
  }

  const handleClose = () => {
    setMobileFiltersOpen(false);
  };

  const handleOpen = () => {
    setMobileFiltersOpen(true);
  };

  const mobileFilterDepartmentOptions: Array<FilterOption> = useMemo(
    () => filters.departments.map(value => ({ label: t(`departments.${value}`), value })),
    [filters.departments]
  );

  const mobileFilterCityOptions: Array<FilterOption> = useMemo(
    () => filters.city.map(value => ({ label: t(`City.${value}`), value })),
    [filters.city]
  );

  const mobileFilters = isMobileFiltersOpen ? (
    <MobileFilters
      values={{ department: query?.department, position: query?.address_city }}
      onClose={handleClose}
      onChange={changeFilters}
    >
      <SelectFilter
        filterKey="department"
        label={t('Department')}
        options={mobileFilterDepartmentOptions}
      />
      <SelectFilter filterKey="position" label={t('Position')} options={mobileFilterCityOptions} />
    </MobileFilters>
  ) : (
    <StyledMobileFiltersButton onClick={handleOpen}>{t('FilterButton')}</StyledMobileFiltersButton>
  );

  const desktopFilters = (
    <>
      <Select label={t('Department')} onChange={setDepartment}>
        {filters.departments.map(department => (
          <option key={department} value={department} selected={department === query?.department}>
            {t(`departments.${department}`)}
          </option>
        ))}
      </Select>
    </>
  );

  return (
    <StyledWrapper isMapVisible={isMapVisible}>
      <StyledInnerWrapper isMapVisible={isMapVisible}>
        <StyledParametersContainer>
          <StyledSelect
            label={t('Name')}
            name="treatment"
            isSearchable
            setDoctor={setDoctor}
            placeholder={t('Doctors')}
            query={query}
            options={doctorNames}
            onChange={setDoctor}
          />

          <StyledSeparationLine />

          <Select label={t('Position')} onChange={setPosition} city="cityForm">
            {filters.city.map(position => (
              <option key={position} value={position} selected={position === query?.address_city}>
                {t(`City.${position}`)}
              </option>
            ))}
          </Select>

          <StyledSeparationLine />

          {isDesktop ? desktopFilters : mobileFilters}
        </StyledParametersContainer>

        <StyledShowMapFloatingButton
          buttonType={CTA_BUTTON}
          label=""
          icon={<FontAwesomeIcon icon={isMapVisible ? faList : faMapMarkerAlt} />}
          onClick={toggleMap}
          inverted
        />
      </StyledInnerWrapper>
    </StyledWrapper>
  );
};

const mapStateToProps = state => ({
  SearchList: state.doctorFinder.searchdata
});

export default compose(
  connect(
    mapStateToProps,
    {
      requestDoctorListSearchBar
    }
  ),
  withTranslation(['Doctors'])
)(SearchBar);
